import { useMemo, useState } from "react";
import { Image as ImageIcon, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ConversationMediaItem } from "@/types/conversation/media";
import { ConversationSectionCard } from "./ConversationSectionCard";


interface ConversationImagesGalleryProps {
  medias: ConversationMediaItem[];
  isLoading?: boolean;
  onDeleteMedia?: (media: ConversationMediaItem) => Promise<boolean>;
  onDeleteAll?: () => Promise<boolean>;
}

const getDescriptionText = (media: ConversationMediaItem): string => {
  const rawMetadata = media.metadata;
  if (!rawMetadata || typeof rawMetadata !== "object" || Array.isArray(rawMetadata)) {
    return "";
  }

  const maybeDescription = (rawMetadata as Record<string, unknown>).image_data;
  return typeof maybeDescription === "string" ? maybeDescription : "";
};

export const ConversationImagesGallery = ({
  medias,
  isLoading = false,
  onDeleteMedia,
  onDeleteAll,
}: ConversationImagesGalleryProps) => {
  const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);
  const [deletingMediaId, setDeletingMediaId] = useState<string | null>(null);
  const [isCleaningAll, setIsCleaningAll] = useState(false);
  const imageMedias = useMemo(() => medias.filter((media) => media.type === "image"), [medias]);
  const selectedMedia = useMemo(
    () => imageMedias.find((media) => media.id === selectedMediaId) || null,
    [imageMedias, selectedMediaId],
  );

  if (isLoading) {
    return (
      <ConversationSectionCard title="Chat Images" genieIcon="eye">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-genie-primary" />
          <span className="text-sm text-gray-500">Loading images...</span>
        </div>
      </ConversationSectionCard>
    );
  }

  if (imageMedias.length === 0) {
    return null;
  }

  const handleDeleteSingle = async (media: ConversationMediaItem) => {
    if (!onDeleteMedia) return;
    setDeletingMediaId(media.id);
    const deleted = await onDeleteMedia(media);
    if (deleted && selectedMediaId === media.id) {
      setSelectedMediaId(null);
    }
    setDeletingMediaId(null);
  };

  const handleDeleteAll = async () => {
    if (!onDeleteAll) return;
    setIsCleaningAll(true);
    const deleted = await onDeleteAll();
    if (deleted) {
      setSelectedMediaId(null);
    }
    setIsCleaningAll(false);
  };

  return (
    <>
      <ConversationSectionCard
        title="Chat Images"
        genieIcon="eye"
        action={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDeleteAll}
            disabled={!onDeleteAll || isCleaningAll}
            className="h-8 px-2 text-xs"
          >
            {isCleaningAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Clean All"}
          </Button>
        }
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {imageMedias.map((media) => (
            <div
              key={media.id}
              className="group rounded-lg overflow-hidden border border-gray-200 bg-gray-50 hover:border-gray-300 transition-colors text-left relative"
            >
              <button
                type="button"
                onClick={() => setSelectedMediaId(media.id)}
                className="w-full text-left"
              >
              <div className="aspect-square bg-gray-100">
                <img src={media.url} alt="Conversation attachment" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="px-2 py-1.5 text-[11px] text-gray-500 flex items-center gap-1">
                <ImageIcon className="h-3 w-3" />
                <span>Open</span>
              </div>
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  void handleDeleteSingle(media);
                }}
                disabled={!onDeleteMedia || deletingMediaId === media.id}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 border border-gray-200 text-gray-600 hover:text-red-600 hover:border-red-200 flex items-center justify-center disabled:opacity-50"
                aria-label="Delete image"
              >
                {deletingMediaId === media.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </button>
            </div>
          ))}
        </div>
      </ConversationSectionCard>

      <Sheet open={!!selectedMedia} onOpenChange={(nextOpen) => !nextOpen && setSelectedMediaId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col h-full gap-4">
          <SheetHeader>
            <SheetTitle>Chat Image</SheetTitle>
            <SheetDescription>Image attached by user during conversation.</SheetDescription>
          </SheetHeader>

          {selectedMedia ? (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              <div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                <img src={selectedMedia.url} alt="Selected conversation attachment" className="w-full h-auto object-contain" />
              </div>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Description</p>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {getDescriptionText(selectedMedia) || "No image description available."}
                </p>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
};

